import { autoinject } from "aurelia-framework";
import type { NavigationSection, SvgIcon } from "../../models/basic.models";
import { Claims } from "../../constants/authentication/claims";
import { RightShipQiRouterConstants } from "../../constants/route.constants";
import { UserClaimsService } from "../basic/user-claims.service";
import { RightStoreService } from "../rightstore/rightstore.service";
import { SubscriptionProductCodes, SubscriptionTypeEnum } from "../../models/rightstore/rightstore.models";
import { CustomerService } from "../basic/customer-client.service";
import { ClaimsRequireTypeEnum } from "../../models/user.models";
import { BaseClaims } from "../../constants/authentication/base-claims";
import { DataType } from "../../models/queries.models";
import type { RouteConfig } from "aurelia-router";
import { VoyageDataSubmissionService } from "../voyage-data-submission/voyage-data-submission.service";
import { UserClientService } from "../basic/user-client.service";
import { ConfigService } from "../basic/config.service";

@autoinject()
export class RoutesService { 

    constructor(private userClaimsService: UserClaimsService, private rightStoreService: RightStoreService, private customerService: CustomerService,
        private voyageDataSubmissionService: VoyageDataSubmissionService, private userClientService: UserClientService,
        private configService: ConfigService) {

    }

    //Get routes
    public async getRoutes(): Promise<RouteConfig[]> {
        const list: RouteConfig[] = [];
        const navigationSections = await this.getAppMenu();
        
        //Get all routes from the navigation sections
        navigationSections.forEach(navigationSection => navigationSection.menuTabs.forEach(menuTab => {
            if (menuTab && menuTab.routes) {
                menuTab.routes.forEach(route => {
                    if (route.name) {
                        route.nav = true;
                        list.push(route);
                        if (route.createNewRoute) {
                            list.push(route.createNewRoute);
                        }
                        if (route.editRoute && typeof (route.editRoute) != "string") {
                            list.push(route.editRoute);
                        }
                    }
                });
            }
        }));

        return list;
    }

    public async getAppMenu(): Promise<NavigationSection[]> {
        const customerId = await this.customerService.getCustomerId();
        const subscriptionResponse = customerId > 0 ? await this.rightStoreService.getCustomerSubscription(customerId, SubscriptionProductCodes.informationServices) : null;
        const isRightShipUser = this.userClaimsService.hasPermission(Claims.RightShipUser);
        const hasCaEnrollment = await this.voyageDataSubmissionService.hasCaEnrollment();
        const hasMepPermission = this.userClaimsService.hasPermission(Claims.CanViewPortHome) || this.userClaimsService.hasPermission(Claims.CanViewPortDashboard);

        //Ship Owner and Ship Manager customer type are able to access the My Vessels portal DEL-15937
        const customerTypes = ["ship owner", "ship manager"];
        const currentUserCustomerType = this.userClientService.currentUser?.businessUnit?.customerType;
        const showRightFleetStarterLink = customerTypes.includes(currentUserCustomerType?.toLowerCase()) || this.userClaimsService.hasPermission(Claims.ShowRightFleetStarterLink);

        const navigationSections: NavigationSection[] = [
            {
                title: "Products",
                icon: "navigation-products",
                isOpen: true, 
                isVisible: true,
                isSectionHeadVisible: isRightShipUser,
                menuTabs: [

                    //Home
                    {
                        key: "Home",
                        title: "Home",
                        icon: <SvgIcon>{
                            d: 'm19.61,7.21L10.61.21c-.36-.28-.87-.28-1.23,0L.39,7.21c-.24.19-.39.48-.39.79v11c0,1.66,1.34,3,3,3h14c1.66,0,3-1.34,3-3V8c0-.31-.14-.6-.39-.79Zm-7.61,12.79h-4v-8h4v8Zm6-1c0,.55-.45,1-1,1h-3v-9c0-.55-.45-1-1-1h-6c-.55,0-1,.45-1,1v9h-3c-.55,0-1-.45-1-1v-10.51L10,2.27l8,6.22v10.51Z',
                            viewBox: ' 0 0 20 22'
                        },
                        isVisible: true,
                        routes: [{ 
                            name: RightShipQiRouterConstants.Home,
                            href: this.configService.config.urls.myVesselsUrl,
                            route: "",
                            title: "Home",
                            auth: true,
                            navigationStrategy: () => {},
                            isVisible: showRightFleetStarterLink
                        }]
                    },
                    //Activities
                    {
                        key: "Tasks",
                        title: "Tasks",
                        icon: <SvgIcon>{
                            d: 'M11,0h0.2h0.1l0,0h0.1l0,0h0.1l0,0h0.1l0,0l0,0l0.1,0.1l6,6l0.1,0.1l0,0l0,0l0,0.1l0,0v0.1l0,0v0.1l0,0c0,0,0,0.1,0,0.1V19c0,1.7-1.3,3-3,3H3c-1.7,0-3-1.3-3-3V3c0-1.7,1.3-3,3-3H11z M10,2H3C2.4,2,2,2.4,2,3v16c0,0.6,0.4,1,1,1h12c0.6,0,1-0.4,1-1V8h-5c-0.5,0-0.9-0.4-1-0.9V2z M5,15h8c0.6,0,1,0.4,1.1,0.9c0,0.6-0.4,1-0.9,1.1H5c-0.6,0-1-0.4-1.1-0.9c0-0.6,0.4-1,0.9-1.1H5z M13,11c0.6,0,1,0.4,1.1,0.9c0,0.6-0.4,1-0.9,1.1H5c-0.6,0-1-0.4-1.1-0.9c0-0.6,0.4-1,0.9-1.1H13z M7,7c0.6,0,1,0.4,1,1S7.6,9,7,9H5C4.4,9,4,8.6,4,8s0.4-1,1-1H7z M17.8,6.3L17.8,6.3L17.8,6.3z M12,3.4V6h2.6L12,3.4z M11,0L11,0z',
                            viewBox: ' 0 0 18 22'
                        },
                        isVisible: true,
                        routes: [{
                            name: RightShipQiRouterConstants.Activities,
                            moduleId: "views/pages/admin/activities/activities",
                            route: "list/activities",
                            title: "Tasks",
                            auth: true,
                            claimsRequired: [
                                Claims.CanAccessVETs,
                                Claims.CanAccessForms,
                                Claims.CanAccessPSCs,
                                Claims.CanAccessINCs,
                                Claims.CanAccessFBRs,
                                Claims.CanAccessCFRs,
                                Claims.CanAccessEnvironmentals,
                                Claims.CanAccessRightShipInspections,
                                Claims.CanAccessWetInspections,
                                Claims.CanAccessCargillInspections,
                                Claims.CanAccessDataSubmissions,
                                Claims.CanAccessCarbonAccounting,
                                Claims.CanAccessSafetyRatingReviews,
                                Claims.CanAccessAutoScreening,
                                Claims.CanAccessTradeCheck,
                                Claims.CanAccessVesselHealthCheck,
                                Claims.CanAccessPreArrivalCheck
                            ],
                            claimsRequireType: ClaimsRequireTypeEnum.RequireAny
                        }]
                    },
                    //Vessels
                    {
                        key: "Vessels",
                        title: "Vessels",
                        icon: <SvgIcon>{
                            d: 'M2.8,12.3h9.4c0.1,0,0.2,0.1,0.2,0.1V13c0,0.1-0.1,0.2-0.1,0.2H2.8c-0.1,0-0.2-0.1-0.2-0.1v-0.6C2.6,12.4,2.7,12.3,2.8,12.3L2.8,12.3z M13.5,10.7c0.1,0,0.2,0.1,0.2,0.1v0.6c0,0.1-0.1,0.2-0.1,0.2H1.5c-0.1,0-0.2-0.1-0.2-0.1v-0.6c0-0.1,0.1-0.2,0.1-0.2L13.5,10.7L13.5,10.7z M5.4,0c0.1,0,0.2,0.1,0.2,0.1v0.6c0,0.1-0.1,0.2-0.1,0.2H4.8v0.9h1.6c0.6,0,1,0.4,1,1l0,0V5h7.1c0.1,0,0.1,0,0.2,0.1c0,0,0,0.1,0,0.1l0,0L13,9.2h1.8c0.1,0,0.2,0.1,0.2,0.1v0.6c0,0.1-0.1,0.2-0.1,0.2H0.2C0.1,10.1,0,10,0,9.9V9.4c0-0.1,0.1-0.2,0.1-0.2H2L0.3,5.2c0-0.1,0-0.1,0-0.2C0.3,5,0.4,5,0.4,5h1.4V2.9c0-0.3,0.1-0.5,0.3-0.7C2.3,2,2.5,1.9,2.7,1.9h0.1h1.1V0.9H3.3c-0.1,0-0.2-0.1-0.2-0.2V0.2C3.1,0.1,3.2,0,3.3,0H5.4z M13.5,5.9H1.6L3,9.2h9L13.5,5.9z M6.5,2.7H2.8c0,0-0.1,0-0.1,0.1V5h3.8V2.9C6.6,2.8,6.5,2.8,6.5,2.7L6.5,2.7z M5.8,3.1c0.1,0,0.2,0.1,0.2,0.2l0,0v0.8c0,0.1-0.1,0.2-0.2,0.2H5.2C5.1,4.3,5,4.2,5,4.1l0,0V3.3c0-0.1,0.1-0.2,0.2-0.2c0,0,0,0,0,0H5.8L5.8,3.1z',
                            viewBox: '0 0 15.1 13.2'
                        },
                        isVisible: true,
                        routes: [{
                            name: RightShipQiRouterConstants.Vessels,
                            moduleId: "views/pages/modules/vessels/vessels",
                            route: "list/vessels",
                            title: "Vessels",
                            auth: true,
                            claimsRequired: [Claims.CanAccessVessels]
                        }]
                    },
                    //Companies
                    {
                        key: "Companies",
                        title: "Companies",
                        icon: <SvgIcon>{
                            d: 'M4.7,13.3H2c-1.1,0-2-0.9-2-2V4.7c0-1.1,0.9-2,2-2h2V2c0-1,0.8-1.9,1.8-2h2.8c1.1,0,2,0.9,2,2l0,0v0.7h2c1.1,0,2,0.9,2,2v6.7c0,1.1-0.9,2-2,2H4.7z M4,4H2C1.6,4,1.3,4.3,1.3,4.7l0,0v6.7C1.3,11.7,1.6,12,2,12c0,0,0,0,0,0h2V4z M9.3,4h-4v8h4V4z M12.7,4h-2v8h2c0.4,0,0.7-0.3,0.7-0.7V4.7C13.3,4.3,13,4,12.7,4z M8.7,1.3H6C5.6,1.3,5.3,1.6,5.3,2c0,0,0,0,0,0l0,0v0.7h4V2C9.3,1.7,9.1,1.4,8.7,1.3L8.7,1.3z',
                            viewBox: '0 0 14.7 13.3'
                        },
                        isVisible: true,
                        routes: [{
                            name: RightShipQiRouterConstants.Companies,
                            moduleId: "views/pages/modules/company/companies",
                            route: "list/companies",
                            title: "Companies",
                            auth: true,
                            claimsRequired: [Claims.CanAccessCompanies]
                        }]
                    },
                    //Crew Welfare
                    {
                        key: "CrewWelfare",
                        title: "Crew Welfare",
                        icon: <SvgIcon>{
                            d: 'M22.9388 13.7832C22.7538 13.5803 22.5376 13.424 22.3021 13.3197V10.8015C22.3021 10.2116 22.2448 9.60776 22.1316 9.00651C22.0217 8.42239 21.8586 7.83743 21.6468 7.26793C21.2297 6.1467 20.6164 5.07552 19.8732 4.17006C19.4946 3.70886 19.0809 3.28752 18.6435 2.9176C18.1928 2.53653 17.7142 2.20761 17.2207 1.94011C17.1825 1.91939 17.1441 1.8991 17.1058 1.87912C17.1028 1.87754 17.0998 1.87607 17.0968 1.87449C16.2536 1.43653 15.3778 1.17691 14.4851 1.09963C14.4029 0.902048 14.2884 0.720241 14.1444 0.562198C13.8277 0.214777 13.4064 0.0234013 12.9583 0.0234013H11.0417C10.5935 0.0234013 10.1723 0.214777 9.85563 0.562198C9.71159 0.720241 9.59707 0.902153 9.51495 1.09963C8.62456 1.1767 7.75104 1.43517 6.90981 1.87102C6.90368 1.87407 6.89755 1.87722 6.89151 1.88048C6.85404 1.90004 6.81657 1.91981 6.77919 1.94011C6.28574 2.20761 5.80706 2.53653 5.35645 2.9176C4.91907 3.28752 4.50535 3.70886 4.12672 4.17006C3.38343 5.07552 2.7701 6.1467 2.35313 7.26793C2.14134 7.83733 1.97823 8.42228 1.86831 9.00651C1.75522 9.60776 1.69782 10.2116 1.69782 10.8015V13.3197C1.46226 13.424 1.24606 13.5803 1.0611 13.7832C0.699333 14.1803 0.5 14.7085 0.5 15.2705V16.5849C0.5 16.9932 0.782804 17.3291 1.14525 17.3694C1.1694 17.3722 1.19393 17.3735 1.21875 17.3735C3.26604 17.3735 4.34464 17.8964 5.59373 18.5019C7.04455 19.2052 8.68905 20.0023 12 20.0023C15.3109 20.0023 16.9554 19.2052 18.4063 18.5019C19.6553 17.8964 20.734 17.3735 22.7812 17.3735C23.1782 17.3735 23.5 17.0204 23.5 16.5849V15.2705C23.5 14.7085 23.3007 14.1803 22.9388 13.7832ZM6.5 4.50232L6.48958 7.12124C6.48958 7.55678 6.90306 7.90988 7.3 7.90988C7.69694 7.90988 8.01828 7.55678 8.01828 7.12124L8 3.50232C8.45847 3.30442 8.86634 3.17679 9.36458 3.10792V8.17276C9.36458 8.6083 9.75306 8.9614 10.15 8.9614C10.5469 8.9614 11 8.6083 11 8.17276V3.00232C11 2.80232 11.2 2.50232 11.5 2.50232H12.5C12.8 2.50232 13 2.80232 13 3.00232V8.17276C13 8.6083 13.4531 9.00232 13.85 9.00232C14.2469 9.00232 14.6354 8.6083 14.6354 8.17276V3.10792C15.1337 3.1769 15.5415 3.30442 16 3.50232V7.12124C16 7.55678 16.3947 7.90988 16.7917 7.90988C17.1886 7.90988 17.5104 7.55678 17.5104 7.12124L17.5 4.50232C19.5285 6.08643 20.4177 8.31103 20.4177 10.8015V13.1675H3.60087V10.8015C3.60087 8.31103 4.4715 6.08643 6.5 4.50232ZM17.8254 17.0591C16.4401 17.7306 14.9163 18.2567 11.9086 18.2567C8.90101 18.2567 7.46859 17.5623 6.08323 16.8908C5.4443 16.5811 4.78363 16.2608 4 16.0234C3.31546 15.816 2.70905 15.8622 1.9375 15.8168V15.7734C1.9375 15.4835 2.15245 15.2734 2.41667 15.2734L12 15.2705L21.5833 15.2734C21.8475 15.2734 22.0625 15.4835 22.0625 15.7734V15.8168C21.2909 15.8622 20.5933 15.9844 19.9086 16.1917C19.1249 16.4292 18.4642 16.7493 17.8254 17.0591Z',
                            viewBox: ' 0 0 24 20'
                        },
                        isVisible: true,
                        routes: [{ 
                            name: "Crew Welfare",
                            href: this.configService.config.urls.crewWelfareUrl,
                            route: "",
                            title: "Crew Welfare",
                            auth: true,
                            navigationStrategy: () => {},
                            isVisible: true
                        }]
                    },
                    //PSC RiskIQ
                    {
                        key: "PscRiskiq",
                        title: "PSC RiskIQ",
                        icon: <SvgIcon>{
                            d: 'M14 15.8377L8.94868 0.683772C8.64478 -0.227924 7.35522 -0.227924 7.05132 0.683772L4.27924 9H1C0.447715 9 0 9.44771 0 10C0 10.5523 0.447715 11 1 11H5C5.43043 11 5.81257 10.7246 5.94868 10.3162L8 4.16228L13.0513 19.3162C13.3552 20.2279 14.6448 20.2279 14.9487 19.3162L17.7208 11H21C21.5523 11 22 10.5523 22 10C22 9.44771 21.5523 9 21 9H17C16.5696 9 16.1874 9.27543 16.0513 9.68377L14 15.8377Z',
                            viewBox: ' 0 0 22 20'
                        },
                        isVisible: true,
                        routes: [{
                            name: "PSC RiskIQ",
                            href: this.configService.config.urls.pscRiskIqUrl,
                            route: "",
                            title: "PSC RiskIQ",
                            auth: true,
                            navigationStrategy: () => {},
                            isVisible: true,
                            claimsRequired: [Claims.CanAccessPSCRiskIQMenu]
                        }]
                    },
                    //Lists & alerts
                    {
                        key: "Lists",
                        title: "Lists",
                        routes: [
                            {
                                name: RightShipQiRouterConstants.Lists,
                                moduleId: "views/pages/admin/lists/lists",
                                route: "list/lists",
                                title: "List & Alerts",
                                icon: "list",
                                auth: true,
                                claimsRequired: [Claims.CanAccessLists]
                            }
                        ],
                        icon: <SvgIcon>{
                            d: 'M21.89,15.55a1,1,0,0,1-.34,1.29l-.1.05-10,5a1,1,0,0,1-.77.06l-.13-.06-10-5a1,1,0,0,1,.79-1.83l.11,0L11,19.88l9.55-4.77A1,1,0,0,1,21.89,15.55Zm-1.34-5.44a1,1,0,1,1,.9,1.78h0l-10,5a1,1,0,0,1-.9,0h0l-10-5a1,1,0,0,1,.9-1.78h0L11,14.88Zm-9.1-10,10,5a1,1,0,0,1,0,1.78l-10,5a1,1,0,0,1-.9,0l-10-5a1,1,0,0,1,0-1.78l10-5A1,1,0,0,1,11.45.11Zm-.45,2L3.24,6,11,9.88,18.76,6Z',
                            viewBox: '0 0 22 22'
                        },
                        isVisible: true,
                        lockedFeature: (subscriptionResponse?.customerSubscription?.subscriptionTypeId == SubscriptionTypeEnum.LimitedSubscription || 
                            subscriptionResponse?.customerSubscription?.isExpired ) && !subscriptionResponse?.customerSubscription?.maxVesselsCount
                    },
                    //Port risk
                    {
                        key: "Ports and Terminals" ,
                        title: "Ports and Terminals" ,
                        icon: <SvgIcon>{
                            d: "M17.7285 10.6016L15.7656 8.63867C15.6191 8.49219 15.4141 8.49219 15.2676 8.63867L13.3047 10.6016C13.0703 10.8359 13.2461 11.1875 13.5391 11.1875H14.8574C14.2715 13.1211 12.1328 14.2637 10.1406 14.4395V7.20312H11.8984C12.0742 7.20312 12.25 7.05664 12.25 6.85156V6.14844C12.25 5.97266 12.0742 5.79688 11.8984 5.79688H10.1406V5.47461C11.0781 5.18164 11.7812 4.27344 11.7812 3.21875C11.7812 1.92969 10.7266 0.875 9.4375 0.875C8.11914 0.875 7.09375 1.92969 7.09375 3.21875C7.09375 4.27344 7.76758 5.18164 8.73438 5.47461V5.79688H6.97656C6.77148 5.79688 6.625 5.97266 6.625 6.14844V6.85156C6.625 7.05664 6.77148 7.20312 6.97656 7.20312H8.73438V14.4395C6.71289 14.2637 4.57422 13.1211 3.98828 11.1875H5.30664C5.59961 11.1875 5.77539 10.8359 5.54102 10.6016L3.57812 8.63867C3.43164 8.49219 3.22656 8.49219 3.08008 8.63867L1.11719 10.6016C0.882812 10.8359 1.05859 11.1875 1.35156 11.1875H2.55273C3.13867 14.1172 6.30273 15.875 9.4375 15.875C12.748 15.875 15.7363 13.9707 16.293 11.1875H17.4941C17.7871 11.1875 17.9629 10.8359 17.7285 10.6016ZM9.4375 2.28125C9.93555 2.28125 10.375 2.7207 10.375 3.21875C10.375 3.74609 9.93555 4.15625 9.4375 4.15625C8.91016 4.15625 8.5 3.74609 8.5 3.21875C8.5 2.7207 8.91016 2.28125 9.4375 2.28125Z",
                            viewBox: '0 0 18 16'
                        },
                        isVisible: true,
                        routes: [
        
                            //Port Risk Profile
                            {
                                name: RightShipQiRouterConstants.PortRiskProfile,
                                moduleId: "views/pages/modules/ports-and-terminals/port-risk-profile/port-risk-profile",
                                route: ["port-risk"],
                                title: 'Port Risk',
        
                                //DEL-5118 - Set as the main root for now
                                // route: ["port-risk-profile"],
                                // title: this.i18N.tr('port-risk-profile'),
                                iconType: null,
                                icon: null,
                                auth: true,
                                claimsRequired: [Claims.CanAccessPortsAndTerminals]
                            },
                            //DEL-5118 - Hidden for now
                            //Feedback Reports
                            // {
                            //     name: RightShipQiRouterConstants.RouteNames.FeedbackReports,
                            //     moduleId: "views/pages/modules/ports-and-terminals/feedback-reports/feedback-reports",
                            //     route: ["feedback-reports"],
                            //     title: this.i18N.tr('feedback-reports'),
                            //     iconType: null,
                            //     icon: null,
                            //     auth: true,
                            //     claimsRequired: []
                            // }
                        ]
                    },
                    //MEP
                    {
                        key: "mep",
                        title: "Maritime Emissions Portal",
                        icon: <SvgIcon>{
                            d: "M18.0064 6.00111C21.3201 6.00111 24.0064 8.6874 24.0064 12.0011C24.0064 15.3148 21.3201 18.0011 18.0064 18.0011L9.00712 18.0011C4.32922 18.0046 0.428548 14.4238 0.0326909 9.76272C-0.363166 5.10159 2.87778 0.914317 7.48928 0.128882C11.8408 -0.61227 16.0435 1.90883 17.4861 6.00111H18.0064ZM18.0064 8.00111H16.7464C16.2904 8.00111 15.8921 7.69263 15.7781 7.25111C14.8685 3.72827 11.4118 1.48959 7.82509 2.10049C4.23837 2.71138 1.71763 5.96815 2.02552 9.59347C2.33341 13.2188 5.36726 16.0038 9.00637 16.0011H18.0064C20.2155 16.0011 22.0064 14.2103 22.0064 12.0011C22.0064 9.79197 20.2155 8.00111 18.0064 8.00111Z",
                            viewBox: '0 0 24 18'
                        },
                        isVisible: hasMepPermission,
                        routes: [
                            {
                                moduleId: "views/pages/modules/mep/mep",
                                name: RightShipQiRouterConstants.MEP,
                                href: "mep",
                                route: ["mep/:step?"],
                                title: `Maritime Emissions Portal`,
                                auth: true,
                                claimsRequired: [Claims.CanViewPortHome, Claims.CanViewPortDashboard],
                                claimsRequireType: ClaimsRequireTypeEnum.RequireAny
                            }
                        ]
                    },
                    //Vessel comparison
                    {
                        key: "VesselComparison",
                        title: "Vessel Comparison",
                        icon: <SvgIcon>{
                            d: 'm0,11v-2c0-2.76,2.24-5,5-5h11.59l-2.29-2.29c-.39-.39-.39-1.02,0-1.41.39-.39,1.02-.39,1.41,0l4,4c.09.09.17.2.22.33.1.24.1.52,0,.76-.05.12-.12.23-.22.33l-4,4c-.39.39-1.02.39-1.41,0s-.39-1.02,0-1.41l2.29-2.29H5c-1.66,0-3,1.34-3,3v2c0,.55-.45,1-1,1s-1-.45-1-1Zm19,1c-.55,0-1,.45-1,1v2c0,1.66-1.34,3-3,3H3.41l2.29-2.29c.39-.39.39-1.02,0-1.41-.39-.39-1.02-.39-1.41,0L.29,18.29c-.09.09-.17.2-.22.33-.1.24-.1.52,0,.76.05.12.12.23.22.33l4,4c.39.39,1.02.39,1.41,0,.39-.39.39-1.02,0-1.41l-2.29-2.29h11.59c2.76,0,5-2.24,5-5v-2c0-.55-.45-1-1-1Z',
                            viewBox: '0 0 20 24'
                        },
                        isVisible: true,
                        routes: [
                            {
                                name: RightShipQiRouterConstants.VesselComparison,
                                moduleId: "views/pages/modules/vessel-comparison/vessel-comparison",
                                route: "vessel-comparison",
                                title: "Vessel Comparison",
                                icon: "clouds",
                                auth: true,
                                claimsRequired: [Claims.CanAccessVesselComparison]
                            }
                        ]
                    },
                    //Rightship insights
                    {
                        key: "RightshipInsights",
                        title: "rightship-insights",
                        routes: [
                            {
                                name: RightShipQiRouterConstants.RightshipInsights,
                                moduleId: "views/pages/modules/rightship-insights/rightship-insights",
                                route: "rightship-insights",
                                title: "RightShip Insights",
                                icon: "arrow-increase",
                                auth: true,
                                claimsRequired: [Claims.CanAccessRightshipInsights]
                            }
                        ],
                        icon: <SvgIcon>{
                            d: 'M22.9,8H23c0.6,0,1-0.4,1-1V0.9c0,0,0,0,0-0.1l0,0c0,0,0,0,0-0.1l0,0c0,0,0,0,0-0.1c0,0,0,0,0-0.1V0.6l0,0c0-0.1-0.1-0.2-0.2-0.2l0,0l-0.2-0.2l0,0l-0.1-0.1l0,0h-0.1h0h-0.1l0,0L23.2,0H17c-0.6,0-1,0.4-1,1s0.4,1,1,1h3.6l-7.1,7.1L9.2,4.8c-0.4-0.4-1-0.4-1.4,0c0,0,0,0,0,0l-7.5,7.5c-0.4,0.4-0.4,1,0,1.4s1,0.4,1.4,0l6.8-6.8l4.3,4.3c0.4,0.4,1,0.4,1.4,0c0,0,0,0,0,0L22,3.4V7C22,7.5,22.4,7.9,22.9,8z',
                            viewBox: '0 0 24 14'
                        },
                        isVisible: true
                    },
                    //Carbon accounting
                    {
                        key: "Carbon Accounting",
                        title: "Carbon Accounting",
                        icon: <SvgIcon>{
                            d: "M0 11C0 17.0751 4.92487 22 11 22C17.0751 22 22 17.0751 22 11C22 4.92487 17.0751 0 11 0C4.92487 0 0 4.92487 0 11ZM20 11C20 15.9706 15.9706 20 11 20C6.02944 20 2 15.9706 2 11C2 6.02944 6.02944 2 11 2C15.9706 2 20 6.02944 20 11ZM14.6294 5.94064H17.0449C17.1972 5.93353 17.3462 5.98605 17.4602 6.08699C17.5742 6.18793 17.6442 6.32932 17.6552 6.48101V15.5472C17.6498 15.6205 17.6299 15.692 17.5967 15.7576C17.5634 15.8232 17.5175 15.8816 17.4616 15.9294C17.4057 15.9772 17.3408 16.0135 17.2708 16.0363C17.2008 16.059 17.1269 16.0677 17.0535 16.0618H14.638C14.4893 16.0715 14.3427 16.0233 14.2289 15.9273C14.1151 15.8313 14.043 15.6951 14.0277 15.5472V6.48101C14.0387 6.33086 14.1075 6.19078 14.2196 6.09007C14.3318 5.98935 14.4786 5.93581 14.6294 5.94064ZM16.4432 15.0583V7.0128H15.2311V15.0583H16.4432ZM9.78988 8.74544H12.2053C12.3526 8.73806 12.4971 8.78732 12.609 8.88307C12.721 8.97881 12.7918 9.11374 12.8071 9.26007V15.5472C12.8016 15.6205 12.7817 15.692 12.7485 15.7576C12.7153 15.8232 12.6694 15.8816 12.6135 15.9294C12.5575 15.9772 12.4927 16.0136 12.4227 16.0363C12.3526 16.059 12.2788 16.0677 12.2053 16.0619H9.78988C9.64119 16.0715 9.49454 16.0233 9.38073 15.9273C9.26692 15.8314 9.19482 15.6951 9.17957 15.5472V9.26007C9.18496 9.18599 9.20513 9.11374 9.23889 9.04754C9.27266 8.98135 9.31934 8.92255 9.37619 8.87461C9.43305 8.82667 9.49893 8.79055 9.56997 8.76836C9.64101 8.74618 9.71578 8.73839 9.78988 8.74544ZM11.6036 15.0583V9.78329H10.3916V15.0583H11.6036ZM7.36581 8.05922H4.95034C4.80165 8.04956 4.655 8.09778 4.54119 8.19375C4.42738 8.28971 4.35528 8.42596 4.34003 8.57386V15.5472C4.35528 15.6951 4.42738 15.8313 4.54119 15.9273C4.655 16.0233 4.80165 16.0715 4.95034 16.0618H7.36581C7.43991 16.0689 7.51468 16.0611 7.58572 16.0389C7.65676 16.0167 7.72264 15.9806 7.77949 15.9327C7.83635 15.8847 7.88303 15.8259 7.91679 15.7597C7.95056 15.6935 7.97073 15.6213 7.97612 15.5472V8.57386C7.97073 8.49978 7.95056 8.42752 7.91679 8.36132C7.88303 8.29513 7.83635 8.23633 7.77949 8.18839C7.72264 8.14045 7.65676 8.10433 7.58572 8.08214C7.51468 8.05996 7.43991 8.05217 7.36581 8.05922ZM6.76409 9.09707V15.0583H5.55206V9.09707H6.76409Z",
                            viewBox: '0 0 22 22'
                        },
                        isVisible: isRightShipUser || hasCaEnrollment,
                        routes: [
        
                            //Home
                            {
                                name: RightShipQiRouterConstants.CarbonAccounting,
                                moduleId: "views/pages/modules/carbon-accounting/carbon-accounting",
                                route: ["carbon-accounting"],
                                title: "Home",
                                iconType: null,
                                icon: null,
                                auth: true,
                                claimsRequired: [Claims.CanAccessCarbonAccountingDashboard]
                            },
        
                            //Summary
                            {
                                name: RightShipQiRouterConstants.CarbonAccountingSummary,
                                moduleId: "views/pages/modules/carbon-accounting/carbon-accounting-summary",
                                route: ["carbon-accounting-summary"],
                                title: "Summary",
                                iconType: null,
                                icon: null,
                                auth: true,
                                claimsRequired: [Claims.CanAccessCarbonAccountingSummaryPage, Claims.CanAccessCarbonAccountingDashboard],
                                claimsRequireType: ClaimsRequireTypeEnum.RequireAll
                            },
        
                            //Voyage records
                            {
                                name: RightShipQiRouterConstants.CarbonAccountingVoyageRecords,
                                moduleId: "views/pages/modules/carbon-accounting/carbon-accounting-voyage-records",
                                route: ["carbon-accounting-voyage-records"],
                                title: "Voyage Records",
                                iconType: null,
                                icon: null,
                                auth: true,
                                claimsRequired: [Claims.CanAccessCarbonAccountingVoyageRecordsPage, Claims.CanAccessCarbonAccountingDashboard],
                                claimsRequireType: ClaimsRequireTypeEnum.RequireAll
                            },
        
                            //Emissions intensity
                            {
                                name: RightShipQiRouterConstants.CarbonAccountingEmissionsIntensity,
                                moduleId: "views/pages/modules/carbon-accounting/carbon-accounting-emissions-intensity",
                                route: ["carbon-accounting-emissions-intensity"],
                                title: "Emissions Intensity",
                                iconType: null,
                                icon: null,
                                auth: true,
                                claimsRequired: [Claims.CanAccessCarbonAccountingEmissionsIntensityPage, Claims.CanAccessCarbonAccountingDashboard],
                                claimsRequireType: ClaimsRequireTypeEnum.RequireAll
                            },
        
                            //How carbon accounting can be improved
                            {
                                name: RightShipQiRouterConstants.HowCarbonAccountingCanBeImproved,
                                moduleId: "views/pages/modules/carbon-accounting/how-carbon-accounting-can-be-improved",
                                route: ["actions"],
                                title: "Actions",
                                icon: <SvgIcon>{
                                    d: "M12,0c1.7,0,3,1.3,3,3v0.1c0,0.3,0.2,0.5,0.4,0.6c0.2,0.1,0.5,0.1,0.7-0.1l0.1-0.1c1.2-1.2,3.1-1.2,4.2,0s1.2,3.1,0,4.2h-0.1c-0.2,0.2-0.2,0.5-0.1,0.7c0,0.1,0.1,0.1,0.1,0.2c0.1,0.2,0.3,0.3,0.5,0.3H21c1.7,0,3,1.3,3,3s-1.3,3-3,3h-0.1c-0.4,0-0.7,0.3-0.6,0.7c0,0.2,0.1,0.3,0.2,0.4l0.1,0.1c1.2,1.2,1.2,3.1,0,4.2s-3.1,1.2-4.2,0l0-0.1c-0.2-0.2-0.5-0.2-0.7-0.1c-0.2,0.1-0.4,0.3-0.4,0.6V21c0,1.7-1.3,3-3,3s-3-1.3-3-3c0-0.3-0.2-0.6-0.5-0.7c-0.2-0.1-0.5-0.1-0.7,0.1l-0.1,0.1c-1.2,1.2-3.1,1.2-4.2,0s-1.2-3.1,0-4.2l0.1,0c0.2-0.2,0.2-0.5,0.1-0.7c-0.1-0.2-0.3-0.4-0.6-0.4H3c-1.7,0-3-1.3-3-3s1.3-3,3-3c0.3,0,0.6-0.2,0.7-0.5c0.1-0.2,0.1-0.5-0.1-0.7L3.5,7.8c-1.2-1.2-1.2-3.1,0-4.2s3.1-1.2,4.2,0v0.1c0.2,0.2,0.5,0.2,0.7,0.1l0.2-0.1C8.9,3.6,9,3.4,9,3.2V3C9,1.3,10.3,0,12,0z M12,2c-0.6,0-1,0.4-1,1v0.2c0,1.1-0.6,2-1.6,2.4c-0.1,0-0.2,0.1-0.3,0.1c-1,0.3-2,0.1-2.7-0.6L6.3,5C6,4.6,5.3,4.5,4.9,4.9c-0.4,0.4-0.5,1-0.1,1.4c0,0,0.1,0.1,0.1,0.1L5,6.5c0.7,0.8,1,1.9,0.6,2.9c-0.4,1-1.4,1.7-2.5,1.7H3c-0.6,0-1,0.4-1,1s0.4,1,1,1h0.2c1.5,0,2.7,1.2,2.6,2.7c0,0.7-0.3,1.3-0.8,1.8L5,17.7c-0.4,0.4-0.5,1-0.1,1.4c0.4,0.4,1,0.5,1.4,0.1c0,0,0.1-0.1,0.1-0.1L6.5,19c0.8-0.7,1.9-1,2.9-0.6c1,0.4,1.7,1.4,1.7,2.5V21c0,0.6,0.4,1,1,1s1-0.4,1-1v-0.2c0-1.5,1.2-2.7,2.7-2.6c0.7,0,1.3,0.3,1.8,0.8l0.1,0.1c0.4,0.4,1,0.5,1.4,0.1c0.4-0.4,0.5-1,0.1-1.4c0,0-0.1-0.1-0.1-0.1L19,17.5c-1-1.1-1-2.7,0-3.8c0.5-0.5,1.2-0.8,1.9-0.8H21c0.6,0,1-0.4,1-1s-0.4-1-1-1h-0.2c-1.1,0-2-0.6-2.4-1.6c0-0.1-0.1-0.2-0.1-0.3c-0.3-1-0.1-2,0.6-2.7L19,6.3c0.4-0.4,0.5-1,0.1-1.4c-0.4-0.4-1-0.5-1.4-0.1c0,0-0.1,0.1-0.1,0.1L17.5,5c-1.1,1-2.7,1-3.8,0C13.3,4.5,13,3.8,13,3.1V3C13,2.4,12.6,2,12,2z M12,8c2.2,0,4,1.8,4,4s-1.8,4-4,4s-4-1.8-4-4S9.8,8,12,8z M12,14c1.1,0,2-0.9,2-2s-0.9-2-2-2s-2,0.9-2,2S10.9,14,12,14z",
                                    viewBox: '0 0 24 24'
                                },
                                auth: true,
                                claimsRequired: [Claims.CanAccessCarbonAccountingDashboard]
                            }
                        ]
                    }
                ]
            },
            {
                title: "Rightship Settings",
                icon: "navigation-rightship-settings",
                isOpen: false,
                isVisible: isRightShipUser,
                isSectionHeadVisible: isRightShipUser,
                menuTabs: [
                    
                    //Customers
                    {
                        key: "Customers",
                        title: "Customers",
                        icon: <SvgIcon>{
                            d: 'm17.52,8.48c2.07,0,3.76-1.68,3.76-3.76s-1.68-3.76-3.76-3.76-3.75,1.68-3.75,3.76,1.68,3.76,3.75,3.76Zm0-5.84c1.15,0,2.09.93,2.09,2.09s-.93,2.09-2.09,2.09-2.09-.93-2.09-2.09.93-2.09,2.09-2.09Zm6.48,10.42v1.4c0,.5-.41.91-.92.91-.45,0-.83-.32-.9-.77v-.13s-.01-1.41-.01-1.41c0-.96-.75-1.78-1.7-1.86h-.17s-5.56,0-5.56,0c-.3,0-.58.08-.83.21,1.34.73,2.27,2.1,2.36,3.71v.24s0,1.81,0,1.81c0,.5-.41.9-.9.9-.46,0-.83-.34-.89-.78v-.12s0-1.81,0-1.81c0-1.43-1.11-2.61-2.52-2.71h-.19s-7.23,0-7.23,0c-1.43,0-2.61,1.11-2.71,2.52v.19s0,1.81,0,1.81c0,.5-.4.9-.9.9-.46,0-.84-.34-.89-.78v-.12s0-1.81,0-1.81c0-2.42,1.9-4.39,4.28-4.51h.24s7.23,0,7.23,0c.02,0,.03,0,.04,0,.64-.84,1.62-1.39,2.69-1.47h.25s5.56,0,5.56,0c1.95,0,3.57,1.52,3.69,3.47v.22Zm-15.86-4.03c2.5,0,4.52-2.02,4.52-4.52S10.63,0,8.14,0,3.61,2.02,3.61,4.52s2.03,4.52,4.52,4.52Zm0-7.23c1.5,0,2.71,1.22,2.71,2.71s-1.21,2.71-2.71,2.71-2.71-1.21-2.71-2.71,1.21-2.71,2.71-2.71Z',
                            viewBox: '0 0 24 18.08'
                        },
                        isVisible: true,
                        routes: [{
                            name: RightShipQiRouterConstants.Customers,
                            moduleId: "views/pages/modules/customers/customers",
                            route: "list/customers",
                            title: "Customers",
                            auth: true,
                            claimsRequired: [Claims.CanAccessCustomers]
                        }]
                    },
                    //Customer subscription
                    {
                        key: "my-subscription",
                        title: "Customer Subscription",
                        icon: <SvgIcon>{
                            d: 'm24,13c0,.55-.47,1-1.04,1h-9.36c-.57,0-1.04-.45-1.04-1s.47-1,1.04-1h9.36c.57,0,1.04.45,1.04,1ZM13.6,2h9.36c.57,0,1.04-.45,1.04-1s-.47-1-1.04-1h-9.36c-.57,0-1.04.45-1.04,1s.47,1,1.04,1Zm9.36,6h-6.24c-.57,0-1.04.45-1.04,1s.47,1,1.04,1h6.24c.57,0,1.04-.45,1.04-1s-.47-1-1.04-1Zm0-4h-6.24c-.57,0-1.04.45-1.04,1s.47,1,1.04,1h6.24c.57,0,1.04-.45,1.04-1s-.47-1-1.04-1Zm-10.04,3.38c.1-.24.1-.52,0-.76-.05-.12-.12-.23-.22-.33L6.71.29c-.39-.39-1.02-.39-1.41,0s-.39,1.02,0,1.41l4.29,4.29H1c-.55,0-1,.45-1,1s.45,1,1,1h8.59l-4.29,4.29c-.39.39-.39,1.02,0,1.41s1.02.39,1.41,0l6-6c.09-.09.17-.2.22-.33Z',
                            viewBox: ' 0 0 24 14'
                        },
                        isVisible: true,
                        routes: [{
                            name: RightShipQiRouterConstants.MySubscription,
                            moduleId: "views/pages/modules/my-subscription/my-subscription",
                            href: "customer-subscription",
                            route: ["customer-subscription/:step?"],
                            title: "Customer Subscription",
                            auth: true,
                            claimsRequired: [Claims.CanAccessCustomerSubscriptions],
                        }]
                    },
                    //Rules
                    {
                        key: "Rules",
                        title: "Rules",
                        icon: <SvgIcon>{
                            d: "M15,8H9C8.4,8,8,8.4,8,9v6c0,0.6,0.4,1,1,1h6c0.6,0,1-0.4,1-1V9C16,8.4,15.6,8,15,8z M14,14h-4v-4h4V14z M23,13h-2v-3h2.1C23.6,9.9,24,9.5,24,9V8.9C23.9,8.4,23.5,8,23,8h-2V6c0-1.7-1.3-3-3-3h-2V0.9C15.9,0.4,15.5,0,15,0h-0.1h-0.1C14.3,0.1,14,0.5,14,1v2h-4V0.9C9.9,0.4,9.5,0,9,0H8.9C8.4,0.1,8,0.5,8,1v2H6C4.3,3,3,4.3,3,6v2H0.9C0.4,8.1,0,8.5,0,9v0.1C0.1,9.6,0.5,10,1,10h2v3H1c-0.6,0-1,0.4-1,1v0.1C0.1,14.6,0.5,15,1,15h2v3c0,1.7,1.3,3,3,3h2v2.1C8.1,23.6,8.5,24,9,24h0.1c0.5-0.1,0.9-0.5,0.9-1v-2h4v2.1c0.1,0.5,0.5,0.9,1,0.9h0.1c0.5-0.1,0.9-0.5,0.9-1v-2h2c1.7,0,3-1.3,3-3v-3h2.1c0.5-0.1,0.9-0.5,0.9-1v-0.1C23.9,13.4,23.5,13,23,13z M19,18c0,0.6-0.4,1-1,1H6c-0.6,0-1-0.4-1-1V6c0-0.6,0.4-1,1-1h12c0.6,0,1,0.4,1,1V18z",
                            viewBox: '0 0 24 24'
                        },
                        isVisible: true,
                        routes: [
                            {
                                name: RightShipQiRouterConstants.Rules,
                                moduleId: "views/pages/modules/rules/rules",
                                route: "rules",
                                title: "Rules",
                                auth: true,
                                claimsRequired: [Claims.CanAccessRules],
                                claimsRequireType: ClaimsRequireTypeEnum.RequireAny
                            }]
                    },
                    //RFI templates
                    {
                        key: "SubtaskTypes",
                        title: "RFI Templates",
                        icon: <SvgIcon>{
                            d: "M20.3,4.3l-8-4c-0.8-0.4-1.8-0.4-2.7,0l-4,2L5.5,2.4L1.7,4.3C0.6,4.8,0,5.9,0,7v9.5c0,1.1,0.6,2.2,1.6,2.7l8,4c0.8,0.4,1.8,0.4,2.7,0l8-4c1-0.5,1.7-1.5,1.7-2.7V7C22,5.9,21.4,4.8,20.3,4.3z M10.6,2.1c0.3-0.1,0.6-0.1,0.9,0l7.3,3.7L16,7.1L8.2,3.3L10.6,2.1z M10,21.1l-7.4-3.7C2.2,17.3,2,16.9,2,16.5V7.4l8,4V21.1z M3.2,5.8L6,4.4l7.8,3.9L11,9.6L3.2,5.8z M19.5,17.4L12,21.1v-9.8l8-4v9.1C20,16.9,19.8,17.3,19.5,17.4z",
                            viewBox: '0 0 22 23.5'
                        },
                        isVisible: true,
                        routes: [
                            {
                                name: RightShipQiRouterConstants.SubtaskTypes,
                                moduleId: "views/pages/modules/rfi-templates/rfi-templates",
                                route: "list/rfi-templates",
                                title: "RFI Templates",
                                selectionMode: 'none',
                                auth: true,
                                claimsRequired: [Claims.CanAccessSubtaskTypes]
                            }
                        ]
                    },
                    //Inspectors
                    {
                        key: "Inspectors",
                        title: "Inspectors",
                        icon: <SvgIcon>{
                            d: 'M14,0C6.3,0,0,6.3,0,14c0,4.4,2,8.5,5.5,11.1L3.5,40C3.4,41,4,41.8,5,42c0.1,0,0.1,0,0.2,0l0,0c0.3,0,0.7-0.1,0.9-0.2L14,37l7.9,4.7c0.3,0.2,0.6,0.3,0.9,0.2l0,0c1,0,1.7-0.9,1.7-1.8c0-0.1,0-0.1,0-0.2l-2-14.9c6.1-4.7,7.2-13.5,2.5-19.6C22.3,2,18.3,0,14,0z M14.9,33.5c-0.6-0.3-1.2-0.3-1.8,0l-5.6,3.4L8.8,27c3.4,1.3,7.1,1.3,10.4,0l1.3,9.9L14.9,33.5z M19.8,22.7c-0.1,0-0.2,0.1-0.2,0.2C17.9,24,16,24.5,14,24.5C8.2,24.5,3.5,19.8,3.5,14C3.5,8.2,8.2,3.5,14,3.5S24.5,8.2,24.5,14C24.5,17.5,22.7,20.8,19.8,22.7z',
                            viewBox: ' 0 0 28 42'
                        },
                        isVisible: true,
                        routes: [{
                            name: RightShipQiRouterConstants.Inspectors,
                            moduleId: "views/pages/admin/inspectors/inspectors",
                            route: "list/inspectors",
                            title: "Inspectors",
                            auth: true,
                            claimsRequired: [
                                Claims.CanAccessInspectors
                            ],
                            claimsRequireType: ClaimsRequireTypeEnum.RequireAny
                        }]
                    },
                    //Custom reports
                    {
                        key: "CustomReports",
                        title: "Custom Reports",
                        routes: [
                            {
                                name: RightShipQiRouterConstants.CustomReports,
                                moduleId: "views/pages/modules/custom-reports/custom-reports",
                                route: "custom-reports",
                                title: "Custom Reports",
                                auth: true,
                                claimsRequired: [Claims.CanAccessCustomReportsTab],
                            }
                        ],
                        icon: <SvgIcon>{
                            d: 'M8,1v18c0,0.6,0.4,1,1,1h4c0.6,0,1-0.4,1-1V1c0-0.6-0.4-1-1-1H9C8.4,0,8,0.4,8,1z M10,2h2v16h-2V2z M1,20h4c0.6,0,1-0.4,1-1v-8c0-0.6-0.4-1-1-1H1c-0.6,0-1,0.4-1,1v8C0,19.6,0.4,20,1,20z M2,12h2v6H2V12z M21,5h-4c-0.6,0-1,0.4-1,1v13c0,0.6,0.4,1,1,1h4c0.6,0,1-0.4,1-1V6C22,5.4,21.6,5,21,5z M20,18h-2V7h2V18z',
                            viewBox: '-2 0 26 22'
                        },
                        isVisible: true
                    },
                    //Reports
                    {
                        key: "Reports",
                        title: "Reports",
                        routes: [
                            {
                                name: RightShipQiRouterConstants.Dashboard,
                                moduleId: "views/pages/dashboard/dashboard",
                                route: "reports",
                                title: "Reports",
                                selectionMode: 'single',
                                auth: true,
                                claimsRequired: [Claims.Dashboard]
                            }
                        ],
                        icon: <SvgIcon>{
                            d: 'M 18 2 C 17.4 2 17 2.4 17 3 L 17 21 C 17 21.6 17.4 22 18 22 L 22 22 C 22.6 22 23 21.6 23 21 L 23 3 C 23 2.4 22.6 2 22 2 L 18 2 z M 19 4 L 21 4 L 21 20 L 19 20 L 19 4 z M 10 7 C 9.4 7 9 7.4 9 8 L 9 21 C 9 21.6 9.4 22 10 22 L 14 22 C 14.6 22 15 21.6 15 21 L 15 8 C 15 7.4 14.6 7 14 7 L 10 7 z M 11 9 L 13 9 L 13 20 L 11 20 L 11 9 z M 2 12 C 1.4 12 1 12.4 1 13 L 1 21 C 1 21.6 1.4 22 2 22 L 6 22 C 6.6 22 7 21.6 7 21 L 7 13 C 7 12.4 6.6 12 6 12 L 2 12 z M 3 14 L 5 14 L 5 20 L 3 20 L 3 14 z ',
                            viewBox: '0 0 24 24'
                        },
                        isVisible: true
                    },
                ]
            },
            {
                title: "Rightship Dev Tools",
                icon: "navigation-rightship-dev-tools",
                isOpen: false,
                isVisible: isRightShipUser,
                isSectionHeadVisible: isRightShipUser,
                menuTabs: [
                    //Manage lookups
                    {
                        key: "Manage Lookups",
                        title: "Manage Lookups",
                        icon: <SvgIcon>{
                            d: 'M15.7,0a3,3,0,0,1,2.74,1.78l3.47,7.81v0a.32.32,0,0,1,0,.09l0-.11a.94.94,0,0,1,.06.15.29.29,0,0,0,0,.09v0A.57.57,0,0,1,22,10h0v5a3,3,0,0,1-3,3H3a3,3,0,0,1-3-3V9.88H0V10a1,1,0,0,1,0-.17.29.29,0,0,0,0-.09l0,0a.32.32,0,0,1,0-.09v0L3.56,1.78A3,3,0,0,1,6.3,0ZM2,15a1,1,0,0,0,1,1H19a1,1,0,0,0,1-1V11H15.54l-1.71,2.55A1,1,0,0,1,13,14H9a1,1,0,0,1-.83-.45h0L6.46,11H2ZM15.7,2H6.3a1,1,0,0,0-.92.6L2.54,9H7a1,1,0,0,1,.76.35l.07.1L9.54,12h2.92l1.71-2.55A1,1,0,0,1,15,9h4.46L16.62,2.6A1,1,0,0,0,15.7,2Z',
                            viewBox: '0 0 22 20'
                        },
                        isVisible: true,
                        routes: [
                            {
                                name: RightShipQiRouterConstants.ManageLookups,
                                moduleId: "views/pages/admin/manage-lookups/manage-lookups",
                                route: "admin/manage-lookups",
                                title: "Manage Lookups",
                                icon: <SvgIcon>{
                                    d: "M12,0c1.7,0,3,1.3,3,3v0.1c0,0.3,0.2,0.5,0.4,0.6c0.2,0.1,0.5,0.1,0.7-0.1l0.1-0.1c1.2-1.2,3.1-1.2,4.2,0s1.2,3.1,0,4.2h-0.1c-0.2,0.2-0.2,0.5-0.1,0.7c0,0.1,0.1,0.1,0.1,0.2c0.1,0.2,0.3,0.3,0.5,0.3H21c1.7,0,3,1.3,3,3s-1.3,3-3,3h-0.1c-0.4,0-0.7,0.3-0.6,0.7c0,0.2,0.1,0.3,0.2,0.4l0.1,0.1c1.2,1.2,1.2,3.1,0,4.2s-3.1,1.2-4.2,0l0-0.1c-0.2-0.2-0.5-0.2-0.7-0.1c-0.2,0.1-0.4,0.3-0.4,0.6V21c0,1.7-1.3,3-3,3s-3-1.3-3-3c0-0.3-0.2-0.6-0.5-0.7c-0.2-0.1-0.5-0.1-0.7,0.1l-0.1,0.1c-1.2,1.2-3.1,1.2-4.2,0s-1.2-3.1,0-4.2l0.1,0c0.2-0.2,0.2-0.5,0.1-0.7c-0.1-0.2-0.3-0.4-0.6-0.4H3c-1.7,0-3-1.3-3-3s1.3-3,3-3c0.3,0,0.6-0.2,0.7-0.5c0.1-0.2,0.1-0.5-0.1-0.7L3.5,7.8c-1.2-1.2-1.2-3.1,0-4.2s3.1-1.2,4.2,0v0.1c0.2,0.2,0.5,0.2,0.7,0.1l0.2-0.1C8.9,3.6,9,3.4,9,3.2V3C9,1.3,10.3,0,12,0z M12,2c-0.6,0-1,0.4-1,1v0.2c0,1.1-0.6,2-1.6,2.4c-0.1,0-0.2,0.1-0.3,0.1c-1,0.3-2,0.1-2.7-0.6L6.3,5C6,4.6,5.3,4.5,4.9,4.9c-0.4,0.4-0.5,1-0.1,1.4c0,0,0.1,0.1,0.1,0.1L5,6.5c0.7,0.8,1,1.9,0.6,2.9c-0.4,1-1.4,1.7-2.5,1.7H3c-0.6,0-1,0.4-1,1s0.4,1,1,1h0.2c1.5,0,2.7,1.2,2.6,2.7c0,0.7-0.3,1.3-0.8,1.8L5,17.7c-0.4,0.4-0.5,1-0.1,1.4c0.4,0.4,1,0.5,1.4,0.1c0,0,0.1-0.1,0.1-0.1L6.5,19c0.8-0.7,1.9-1,2.9-0.6c1,0.4,1.7,1.4,1.7,2.5V21c0,0.6,0.4,1,1,1s1-0.4,1-1v-0.2c0-1.5,1.2-2.7,2.7-2.6c0.7,0,1.3,0.3,1.8,0.8l0.1,0.1c0.4,0.4,1,0.5,1.4,0.1c0.4-0.4,0.5-1,0.1-1.4c0,0-0.1-0.1-0.1-0.1L19,17.5c-1-1.1-1-2.7,0-3.8c0.5-0.5,1.2-0.8,1.9-0.8H21c0.6,0,1-0.4,1-1s-0.4-1-1-1h-0.2c-1.1,0-2-0.6-2.4-1.6c0-0.1-0.1-0.2-0.1-0.3c-0.3-1-0.1-2,0.6-2.7L19,6.3c0.4-0.4,0.5-1,0.1-1.4c-0.4-0.4-1-0.5-1.4-0.1c0,0-0.1,0.1-0.1,0.1L17.5,5c-1.1,1-2.7,1-3.8,0C13.3,4.5,13,3.8,13,3.1V3C13,2.4,12.6,2,12,2z M12,8c2.2,0,4,1.8,4,4s-1.8,4-4,4s-4-1.8-4-4S9.8,8,12,8z M12,14c1.1,0,2-0.9,2-2s-0.9-2-2-2s-2,0.9-2,2S10.9,14,12,14z",
                                    viewBox: '0 0 24 24'
                                },
                                auth: true,
                                claimsRequired: [Claims.CanAccessManageLookups]
                            }
                        ]
                    },
                    //Activity forms
                    {
                        key: "Activity Forms",
                        title: "Activity Forms",
                        icon: <SvgIcon>{
                            d: 'M12,0c1.1,0,2,0.9,2,2h1c1.6,0,2.9,1.2,3,2.8V19c0,1.6-1.2,2.9-2.8,3H3c-1.6,0-2.9-1.2-3-2.8V5c0-1.6,1.2-2.9,2.8-3H4c0-1.1,0.9-2,2-2H12z M4,4H3C2.5,4,2.1,4.4,2,4.9V19c0,0.5,0.4,0.9,0.9,1H15c0.5,0,0.9-0.4,1-0.9V5c0-0.5-0.4-0.9-0.9-1H14c0,1.1-0.9,2-2,2H6C4.9,6,4,5.1,4,4z M12,2H6v2h6V2z',
                            viewBox: '0 0 18 22'
                        },
                        isVisible: true,
                        routes: [
                            //Client Form Plugin
                            {
                                moduleId: "views/pages/queries/queries",
                                name: RightShipQiRouterConstants.OnboardingPlugins,
                                route: "list/client-form-plugins",
                                entity: "onboardingPlugins",
                                title: "Client Form Plugins",
                                auth: true,
                                hasNavbar: true,
                                claimsRequired: [BaseClaims.GetClientForms],
                                claimsToAdd: [BaseClaims.AddNewClientForm],
                                fixedColumns: [{
                                    name: 'Delete',
                                    title: "Delete",
                                    dataType: DataType.Icon,
                                    parameter: "delete",
                                    //todo
                                    // value: (item, row, colum, dataGridVM) => {
                                    //     let model = new GenericConfirmationDialogModel();
                                    //     model.confirm = "Delete";
                                    //     model.cancel = "Cancel";
                                    //     model.messages.push("All client form requests and data related to this form will be deleted. Are you sure you want to proceed?");
                                    //     model.title = "Confirm Deletion";
                                    //     model.onConfirm = () => {
                                    //         return new Promise<void>((resolve, reject) => {
                                    //             this.onboardingService.deleteOnboardingPlugin({ onboardingPluginId: item.onboardingPluginId })
                                    //                 .then(x => {
                                    //                     if (x.isSuccess) {
                                    //                         this.notificationClientService.show(("{0} has been successfully deleted!").replace('{0}', item.title), '', NotificationType.Success);
                                    //                         this.queriesClientService.clearCache().then(() => {
                                    //                             this.onboardingService.getODataModels().then(x => {
                                    //                                 if (x.entityConfigs) {
                                    //                                     let length = AllEntityTypeConfigurationConstants.length;
                                    //                                     for (let index = length - 1; index >= 0; index++) {
                                    //                                         const element = AllEntityTypeConfigurationConstants[index];
                                    //                                         if (element.navbar == "onboardingrequests") {
                                    //                                             if (!x.entityConfigs.some(z => z.uri == element.uri)) {
                                    //                                                 AllEntityTypeConfigurationConstants.splice(index, 1);
                                    //                                             }
                                    //                                         }
                                    //                                     }
                                    //                                 }

                                    //                                 dataGridVM.reloadData();
                                    //                                 resolve();
                                    //                             });
                                    //                         });
                                    //                     } else {
                                    //                         this.notificationClientService.show("Client form plugin has already been deleted!", '', NotificationType.Warning);
                                    //                         resolve();
                                    //                     }
                                    //                 })
                                    //                 .catch(response => {
                                    //                     if (!RequestClientService.isExceptionHandled(response)) {
                                    //                         this.notificationClientService.show("Error", 'Cannot delete client form plugin because it is referenced in another entity.', NotificationType.Error)
                                    //                     }
                                    //                     reject();
                                    //                 });
                                    //         });
                                    //     }
                                    //     this.dialogClientService.show(DialogServiceConstants.URLs.GenericConfirmationDialog, model, false, false);
                                    // },
                                    sortOrder: 100,
                                    columnOrder: 100,
                                    isHidden: (item) => !item.isDynamic
                                },
                                {
                                    name: "Clone",
                                    title: "Clone",
                                    dataType: DataType.Icon,
                                    sortOrder: 99,
                                    columnOrder: 99,
                                    parameter: "filter_none",
                                    //todo
                                    // value: (item, row, colum, dataGridVM) => {
                                    //     this.dialogClientService.show(DialogServiceConstants.URLs.CloneOnboardingPluginDialog, {
                                    //         itemId: item.onboardingPluginId,
                                    //         actionAfterClone: () => dataGridVM.reloadData()
                                    //     });
                                    // },
                                    isHidden: (item) => !item.isDynamic
                                }],
                            },
                            //Workflow Config
                            {
                                name: RightShipQiRouterConstants.WorkflowConfiguration,
                                moduleId: "views/pages/admin/workflow/workflow-configuration",
                                route: ["workflows-configuration"],
                                title: "Workflows config",
                                //todo
                                // iconType: IconType.Font,
                                // icon: "tune",
                                auth: true,
                                claimsRequired: [BaseClaims.WorkflowsConfig]
                            },
                            //Manage Entities
                            {
                                name: RightShipQiRouterConstants.ManageEntities,
                                moduleId: "views/pages/admin/manage-entities/manage-entities",
                                route: ["admin/manage-entities"],
                                title: "Manage Entities",
                                icon: <SvgIcon>{
                                    d: "M12,0c1.7,0,3,1.3,3,3v0.1c0,0.3,0.2,0.5,0.4,0.6c0.2,0.1,0.5,0.1,0.7-0.1l0.1-0.1c1.2-1.2,3.1-1.2,4.2,0s1.2,3.1,0,4.2h-0.1c-0.2,0.2-0.2,0.5-0.1,0.7c0,0.1,0.1,0.1,0.1,0.2c0.1,0.2,0.3,0.3,0.5,0.3H21c1.7,0,3,1.3,3,3s-1.3,3-3,3h-0.1c-0.4,0-0.7,0.3-0.6,0.7c0,0.2,0.1,0.3,0.2,0.4l0.1,0.1c1.2,1.2,1.2,3.1,0,4.2s-3.1,1.2-4.2,0l0-0.1c-0.2-0.2-0.5-0.2-0.7-0.1c-0.2,0.1-0.4,0.3-0.4,0.6V21c0,1.7-1.3,3-3,3s-3-1.3-3-3c0-0.3-0.2-0.6-0.5-0.7c-0.2-0.1-0.5-0.1-0.7,0.1l-0.1,0.1c-1.2,1.2-3.1,1.2-4.2,0s-1.2-3.1,0-4.2l0.1,0c0.2-0.2,0.2-0.5,0.1-0.7c-0.1-0.2-0.3-0.4-0.6-0.4H3c-1.7,0-3-1.3-3-3s1.3-3,3-3c0.3,0,0.6-0.2,0.7-0.5c0.1-0.2,0.1-0.5-0.1-0.7L3.5,7.8c-1.2-1.2-1.2-3.1,0-4.2s3.1-1.2,4.2,0v0.1c0.2,0.2,0.5,0.2,0.7,0.1l0.2-0.1C8.9,3.6,9,3.4,9,3.2V3C9,1.3,10.3,0,12,0z M12,2c-0.6,0-1,0.4-1,1v0.2c0,1.1-0.6,2-1.6,2.4c-0.1,0-0.2,0.1-0.3,0.1c-1,0.3-2,0.1-2.7-0.6L6.3,5C6,4.6,5.3,4.5,4.9,4.9c-0.4,0.4-0.5,1-0.1,1.4c0,0,0.1,0.1,0.1,0.1L5,6.5c0.7,0.8,1,1.9,0.6,2.9c-0.4,1-1.4,1.7-2.5,1.7H3c-0.6,0-1,0.4-1,1s0.4,1,1,1h0.2c1.5,0,2.7,1.2,2.6,2.7c0,0.7-0.3,1.3-0.8,1.8L5,17.7c-0.4,0.4-0.5,1-0.1,1.4c0.4,0.4,1,0.5,1.4,0.1c0,0,0.1-0.1,0.1-0.1L6.5,19c0.8-0.7,1.9-1,2.9-0.6c1,0.4,1.7,1.4,1.7,2.5V21c0,0.6,0.4,1,1,1s1-0.4,1-1v-0.2c0-1.5,1.2-2.7,2.7-2.6c0.7,0,1.3,0.3,1.8,0.8l0.1,0.1c0.4,0.4,1,0.5,1.4,0.1c0.4-0.4,0.5-1,0.1-1.4c0,0-0.1-0.1-0.1-0.1L19,17.5c-1-1.1-1-2.7,0-3.8c0.5-0.5,1.2-0.8,1.9-0.8H21c0.6,0,1-0.4,1-1s-0.4-1-1-1h-0.2c-1.1,0-2-0.6-2.4-1.6c0-0.1-0.1-0.2-0.1-0.3c-0.3-1-0.1-2,0.6-2.7L19,6.3c0.4-0.4,0.5-1,0.1-1.4c-0.4-0.4-1-0.5-1.4-0.1c0,0-0.1,0.1-0.1,0.1L17.5,5c-1.1,1-2.7,1-3.8,0C13.3,4.5,13,3.8,13,3.1V3C13,2.4,12.6,2,12,2z M12,8c2.2,0,4,1.8,4,4s-1.8,4-4,4s-4-1.8-4-4S9.8,8,12,8z M12,14c1.1,0,2-0.9,2-2s-0.9-2-2-2s-2,0.9-2,2S10.9,14,12,14z",
                                    viewBox: '0 0 24 24'
                                },
                                auth: true,
                                claimsRequired: [Claims.CanAccessManageEntities]
                            },
                            //Client Forms
                            {
                                moduleId: "views/pages/queries/queries",
                                name: RightShipQiRouterConstants.Onboardings,
                                route: "list/client-forms",
                                entity: "onboardings",
                                title: "Client Forms",
                                //todo
                                // iconType: IconType.Font,
                                // icon: "folder_shared",
                                auth: true,
                                hasNavbar: false,
                                claimsRequired: [BaseClaims.GetClientForms],
                                claimsToAdd: [BaseClaims.AddNewClientForm],
                                fixedColumns: [{
                                    name: 'Delete',
                                    title: "Delete",
                                    dataType: DataType.Icon,
                                    parameter: "delete",
                                    //todo
                                    // value: (item, row, colum, dataGridVM) => {
                                    //     let model = new GenericConfirmationDialogModel();
                                    //     model.confirm = this.i18N.tr('delete-text');
                                    //     model.cancel = this.i18N.tr('cancel-text');
                                    //     model.messages.push(this.i18N.tr('are-you-sure-you-want-to-delete-onboarding'));
                                    //     model.title = this.i18N.tr('confirm-delete');
                                    //     model.onConfirm = () => {
                                    //         return new Promise<void>((resolve, reject) => {
                                    //             this.onboardingService.deleteOnboardingConfig({ onboardingConfigId: item.onboardingConfigId })
                                    //                 .then(x => {
                                    //                     if (x.isSuccess) {
                                    //                         this.notificationClientService.show(this.i18N.tr('successful-onboarding-deletion-text').replace('{0}', item.onboardingConfigName), '', NotificationType.Success, 5000);
                                    //                         dataGridVM.reloadData();
                                    //                         resolve();
                                    //                     } else {
                                    //                         this.notificationClientService.show(this.i18N.tr('onboarding-not-found-text'), '', NotificationType.Warning, 5000);
                                    //                         dataGridVM.reloadData();
                                    //                         resolve();
                                    //                     }
                                    //                 })
                                    //                 .catch(response => {
                                    //                     if (!RequestClientService.isExceptionHandled(response)) {
                                    //                         this.notificationClientService.show(this.i18N.tr('error-text'), 'Cannot delete client form because it is referenced in another entity.', NotificationType.Error, 5000);
                                    //                     }
                                    //                     reject();
                                    //                 });
                                    //         });
                                    //     }
                                    //     this.dialogClientService.show(DialogServiceConstants.URLs.GenericConfirmationDialog, model, false, false);
                                    // },
                                    sortOrder: 100,
                                    columnOrder: 100,
                                    isHidden: !this.userClaimsService.hasPermission(BaseClaims.DeleteClientForm)
                                },
                                {
                                    name: "Clone",
                                    title: "Clone",
                                    dataType: DataType.Icon,
                                    sortOrder: 99,
                                    columnOrder: 99,
                                    parameter: "filter_none",
                                    //todo
                                    // value: (item, row, colum, dataGridVM) => {
                                    //     this.dialogClientService.show(DialogServiceConstants.URLs.CloneOnboardingDialog, {
                                    //         itemId: item.onboardingConfigId,
                                    //         actionAfterClone: () => dataGridVM.reloadData()
                                    //     });
                                    // },
                                    isHidden: !this.userClaimsService.hasPermission(BaseClaims.CloneClientForms)
                                }],
                                uri: "Onboardings",
                                addRoute: {
                                    name: RightShipQiRouterConstants.PersistOnboarding,
                                    moduleId: "views/pages/admin/onboarding/onboarding",
                                    route: ["client-form-config/:id"],
                                    isVisible: false,
                                    title: "Create client forms",
                                    //todo
                                    // iconType: IconType.Font,
                                    // icon: "add",
                                    auth: true,
                                    claimsRequired: [],
                                },
                                editRoute: {
                                    moduleId: "views/pages/admin/onboarding/onboarding",
                                    name: RightShipQiRouterConstants.PersistOnboarding,
                                    route: ["client-form-config/:id"],
                                    title: "Manage Client Forms",
                                    auth: true,
                                    isVisible: false,
                                    claimsRequired: [],
                                    editEntityUri: "OnboardingConfigs"
                                }
                            },
                        ]
                    },
                    //Pipeline executions
                    {
                        key: "PipelinesExecutions",
                        title: "Pipeline Executions",
                        icon: <SvgIcon>{
                            d: "M3,10h16c1.7,0,3-1.3,3-3V3c0-1.7-1.3-3-3-3H3C1.3,0,0,1.3,0,3v4C0,8.7,1.3,10,3,10z M2,3c0-0.6,0.4-1,1-1h16c0.6,0,1,0.4,1,1v4c0,0.6-0.4,1-1,1H3C2.4,8,2,7.6,2,7V3z M3,22h16c1.7,0,3-1.3,3-3v-4c0-1.7-1.3-3-3-3H3c-1.7,0-3,1.3-3,3v4C0,20.7,1.3,22,3,22z M2,15c0-0.6,0.4-1,1-1h16c0.6,0,1,0.4,1,1v4c0,0.6-0.4,1-1,1H3c-0.6,0-1-0.4-1-1V15z",
                            viewBox: '0 0 22 22'
                        },
                        isVisible: true,
                        routes: [
                            {
                                hasHeader: true,
                                name: RightShipQiRouterConstants.PipelineExecutions,
                                moduleId: "views/pages/modules/pipeline-executions/pipeline-executions",
                                route: "pipeline-executions",
                                title: "Pipeline Executions",
                                selectionMode: 'none',
                                auth: true,
                                claimsRequired: [Claims.CanAccessPipelineExecutions],
                                editRoute: {
                                    name: RightShipQiRouterConstants.PipelineExecution,
                                    moduleId: "views/pages/modules/pipeline-executions/pipeline-execution",
                                    route: ["pipeline-execution/:name"],
                                    title: "Pipeline Execution",
                                    auth: true,
                                    isVisible: false,
                                    claimsRequired: [Claims.CanAccessPipelineExecutions]
                                },
                            }]
                    },
                    //Pipelines
                    {
                        key: "Pipelines",
                        title: "Pipelines",
                        icon: <SvgIcon>{
                            d: "M3,10h16c1.7,0,3-1.3,3-3V3c0-1.7-1.3-3-3-3H3C1.3,0,0,1.3,0,3v4C0,8.7,1.3,10,3,10z M2,3c0-0.6,0.4-1,1-1h16c0.6,0,1,0.4,1,1v4c0,0.6-0.4,1-1,1H3C2.4,8,2,7.6,2,7V3z M3,22h16c1.7,0,3-1.3,3-3v-4c0-1.7-1.3-3-3-3H3c-1.7,0-3,1.3-3,3v4C0,20.7,1.3,22,3,22z M2,15c0-0.6,0.4-1,1-1h16c0.6,0,1,0.4,1,1v4c0,0.6-0.4,1-1,1H3c-0.6,0-1-0.4-1-1V15z",
                            viewBox: '0 0 22 22'
                        },
                        isVisible: true,
                        routes: [
                            {
                                name: RightShipQiRouterConstants.Pipelines,
                                moduleId: "views/pages/modules/pipelines/pipelines",
                                route: "pipelines",
                                title: "Pipelines",
                                auth: true,
                                claimsRequired: [Claims.CanAccessPipelines]
                            }]
                    },
                    //Email history
                    {
                        key: "EmailHistory",
                        title: "Email History",
                        icon: <SvgIcon>{
                            d: 'M0,3v12c0,1.7,1.3,3,3,3h16c1.7,0,3-1.3,3-3V3c0-1.7-1.3-3-3-3H3C1.3,0,0,1.3,0,3z M20,15c0,0.6-0.4,1-1,1H3c-0.6,0-1-0.4-1-1V4.9l8.4,5.9c0.3,0.2,0.8,0.2,1.1,0L20,4.9V15z M19.9,2.5L11,8.8L2.1,2.5C2.3,2.2,2.6,2,3,2h16C19.4,2,19.7,2.2,19.9,2.5z',
                            viewBox: '-2 0 24 20'
                        },
                        isVisible: true,
                        routes: [
                            {
                                name: RightShipQiRouterConstants.ListEmailHistory,
                                moduleId: "views/pages/modules/email-history/email-history",
                                route: "list/emailHistories",
                                title: "Email History",
                                claimsRequired: [Claims.CanViewEmailHistory]
                            }]
                    },
                    //Survivorship rules
                    {
                        key: "SurvivorshipRules",
                        title: "Survivorship Rules",
                        icon: <SvgIcon>{
                            d: "M15,8H9C8.4,8,8,8.4,8,9v6c0,0.6,0.4,1,1,1h6c0.6,0,1-0.4,1-1V9C16,8.4,15.6,8,15,8z M14,14h-4v-4h4V14z M23,13h-2v-3h2.1C23.6,9.9,24,9.5,24,9V8.9C23.9,8.4,23.5,8,23,8h-2V6c0-1.7-1.3-3-3-3h-2V0.9C15.9,0.4,15.5,0,15,0h-0.1h-0.1C14.3,0.1,14,0.5,14,1v2h-4V0.9C9.9,0.4,9.5,0,9,0H8.9C8.4,0.1,8,0.5,8,1v2H6C4.3,3,3,4.3,3,6v2H0.9C0.4,8.1,0,8.5,0,9v0.1C0.1,9.6,0.5,10,1,10h2v3H1c-0.6,0-1,0.4-1,1v0.1C0.1,14.6,0.5,15,1,15h2v3c0,1.7,1.3,3,3,3h2v2.1C8.1,23.6,8.5,24,9,24h0.1c0.5-0.1,0.9-0.5,0.9-1v-2h4v2.1c0.1,0.5,0.5,0.9,1,0.9h0.1c0.5-0.1,0.9-0.5,0.9-1v-2h2c1.7,0,3-1.3,3-3v-3h2.1c0.5-0.1,0.9-0.5,0.9-1v-0.1C23.9,13.4,23.5,13,23,13z M19,18c0,0.6-0.4,1-1,1H6c-0.6,0-1-0.4-1-1V6c0-0.6,0.4-1,1-1h12c0.6,0,1,0.4,1,1V18z",
                            viewBox: '0 0 24 24'
                        },
                        isVisible: true,
                        routes: [
                            {
                                name: RightShipQiRouterConstants.SurvivorshipRules,
                                moduleId: "views/pages/modules/survivorship-rules/survivorship-rules",
                                route: "survivorship-rules",
                                title: "Survivorship Rules",
                                auth: true,
                                claimsRequired: [Claims.CanAccessSurvivorshipRules]
                            }]
                    },
                    //Data failures
                    {
                        key: "SurvivorshipRuleFailures",
                        title: "Data Failures",
                        icon: <SvgIcon>{
                            d: "M7.3,13.3L9.6,11L7.3,8.7C6.9,8.4,6.9,7.8,7.2,7.4l0.1-0.1c0.4-0.4,1-0.4,1.4,0c0,0,0,0,0,0L11,9.6l2.3-2.3 c0.4-0.4,0.9-0.4,1.3-0.1l0.1,0.1c0.4,0.4,0.4,1,0,1.4c0,0,0,0,0,0L12.4,11l2.3,2.3c0.4,0.4,0.4,0.9,0.1,1.3l-0.1,0.1 c-0.4,0.4-1,0.4-1.4,0c0,0,0,0,0,0L11,12.4l-2.3,2.3c-0.4,0.4-0.9,0.4-1.3,0.1l-0.1-0.1C6.9,14.3,6.9,13.7,7.3,13.3 C7.3,13.3,7.3,13.3,7.3,13.3z M0,11C0,4.9,4.9,0,11,0s11,4.9,11,11s-4.9,11-11,11S0,17.1,0,11z M2,11c0,5,4,9,9,9s9-4,9-9s-4-9-9-9 S2,6,2,11z",
                            viewBox: '0 0 22 22'
                        },
                        isVisible: true,
                        routes: [
                            {
                                name: RightShipQiRouterConstants.SurvivorshipRuleFailures,
                                moduleId: "views/pages/modules/survivorship-rule-failures/survivorship-rule-failures",
                                route: "survivorship-rule-failures",
                                title: "Data Failures",
                                auth: true,
                                claimsRequired: [Claims.CanAccessSurvivorshipRuleFailures]
                            }]
                    },
                    //Scheduled maintenance
                    {
                        key: "Scheduled Maintenances",
                        title: "Scheduled Maintenances",
                        icon: <SvgIcon>{
                            d: 'M14.21,21.75a10,10,0,0,0,7.22-6.42,1,1,0,1,0-1.88-.66,8,8,0,0,1-13.23,3L3.52,15H7a1,1,0,0,0,0-2H1a1,1,0,0,0-.93.62v0A1.1,1.1,0,0,0,0,14v6a1,1,0,0,0,2,0V16.31l2.93,2.76A10,10,0,0,0,12,22,9.58,9.58,0,0,0,14.21,21.75ZM22,9a1,1,0,0,0,.93-.63h0A.89.89,0,0,0,23,8V2a1,1,0,0,0-2,0V5.69L18.07,2.93A10,10,0,0,0,1.57,6.66a1,1,0,1,0,1.88.67,8,8,0,0,1,13.23-3L19.48,7H16a1,1,0,0,0,0,2Z',
                            viewBox: '0 0 23 22'
                        },
                        isVisible: true,
                        routes: [
                            {
                                moduleId: "views/pages/admin/scheduled-maintenances/scheduled-maintenances",
                                name: RightShipQiRouterConstants.ManageScheduledMaintenances,
                                route: "admin/scheduled-maintenances",
                                title: "Scheduled Maintenances",
                                auth: true,
                                claimsRequired: [BaseClaims.ViewScheduledMaintenances]
                            }
                        ]
                    }
                ]
            }
        ]

        return navigationSections;
    }
}